import { useMemo } from "react";
import { format, isSameMonth } from "date-fns";
import { Plus } from "lucide-react";
import { Button, Skeleton, Text } from "@musa.tech/design-system";
import { cn } from "@/lib/utils";
import { ColetaCalendarCard } from "./ColetaCalendarCard";
import {
  dayKey,
  getMonthWeeks,
  startOfToday,
} from "@/utils/coletasCalendar";
import type { DiaCalendario } from "@/hooks/useProximasColetas";
import type { CollectForTable } from "@/hooks/useCollectsApiData";

interface MesViewProps {
  referenceDate: Date;
  byDay: Map<string, DiaCalendario>;
  isLoading: boolean;
  onSelect?: (collect: CollectForTable) => void;
  /** Abre o drawer com todas as coletas do dia (clique no número ou "Ver mais"). */
  onOpenDay?: (dayKey: string) => void;
}

const WEEKDAY_LABELS = ["SEG", "TER", "QUA", "QUI", "SEX", "SÁB", "DOM"];
const MAX_CARDS_PER_DAY = 3;

export const MesView = ({
  referenceDate,
  byDay,
  isLoading,
  onSelect,
  onOpenDay,
}: MesViewProps) => {
  const weeks = useMemo(() => getMonthWeeks(referenceDate), [referenceDate]);
  const todayKey = dayKey(startOfToday());

  if (isLoading) {
    return <Skeleton className="h-[28rem] w-full rounded-lg" />;
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[700px] overflow-hidden rounded-lg border border-border bg-white">
        <div className="grid grid-cols-7 border-b border-border bg-neutral-100">
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className="px-2 py-2 text-center">
              <Text
                variant="label"
                size="small"
                className="uppercase text-neutral-500"
              >
                {label}
              </Text>
            </div>
          ))}
        </div>
        {weeks.map((week, weekIndex) => (
          <div
            key={weekIndex}
            className="grid grid-cols-7 border-b border-border last:border-b-0"
          >
            {week.map((day) => {
              const key = dayKey(day);
              const entry = byDay.get(key);
              const collects = entry?.collects ?? [];
              const total = entry?.total ?? 0;
              const inMonth = isSameMonth(day, referenceDate);
              const isToday = key === todayKey;
              const shown = Math.min(collects.length, MAX_CARDS_PER_DAY);
              const overflow = total - shown;
              return (
                <div
                  key={key}
                  className={cn(
                    "min-h-[7rem] border-r border-border p-1.5 last:border-r-0",
                    isToday
                      ? "bg-primary-50 ring-1 ring-inset ring-primary-300"
                      : !inMonth ? "bg-neutral-50" : "",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onOpenDay?.(key)}
                    className={cn(
                      "mb-1 block rounded px-1 transition-colors hover:bg-primary-50",
                      isToday
                        ? "font-bold text-primary-700"
                        : inMonth
                          ? "text-neutral-700"
                          : "text-neutral-300",
                    )}
                  >
                    <Text variant="label" size="small">
                      {format(day, "d")}
                    </Text>
                  </button>
                  <div className="flex flex-col gap-1">
                    {collects.slice(0, MAX_CARDS_PER_DAY).map((collect) => (
                      <ColetaCalendarCard
                        key={collect.collect_id}
                        collect={collect}
                        onSelect={onSelect}
                      />
                    ))}
                    {overflow > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenDay?.(key)}
                        className="h-auto w-full justify-center gap-1 border-dashed border-primary-300 px-2 py-1.5 text-xs font-semibold text-primary-700 hover:border-primary-400 hover:bg-primary-50"
                      >
                        <Plus className="h-3 w-3" aria-hidden />
                        Ver mais {overflow}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
